import { z } from 'zod';

export const ProcedureFormSchema = z.object({
  code: z.string().min(1, 'Procedure code is required').max(32),
  name: z.string().min(1, 'Procedure name is required').max(300),
  categoryId: z.string().min(1, 'Category is required'),
  billingGranularity: z.enum(['per_step', 'whole']),
  defaultTaxRateBps: z
    .number({ error: 'Tax must be a number' })
    .min(0, 'Tax must be at least 0%')
    .max(100, 'Tax cannot exceed 100%'),
  icdCode: z.string().max(20).optional(),
  description: z.string().max(2000).optional(),
  duration: z.string().max(50).optional(),
  sessions: z.string().max(50).optional(),
  insuranceCovered: z.enum(['yes', 'no', 'partial']),
  toothSelectionMode: z.enum(['none', 'per_tooth', 'optional']),
});

export type ProcedureFormValues = z.infer<typeof ProcedureFormSchema>;

export const StepFormSchema = z.object({
  name: z.string().min(1, 'Step name is required').max(200),
  feePaise: z
    .number({ error: 'Price is required' })
    .int()
    .positive('Price must be greater than zero'),
  durationMinutes: z
    .number({ error: 'Duration is required' })
    .int()
    .min(1, 'Duration must be at least 1 minute'),
  taxRateBps: z.number().int().min(0).max(10000).nullable().optional(),
  minGapDaysFromPrev: z.number().int().min(0).nullable().optional(),
  maxGapDaysFromPrev: z.number().int().min(0).nullable().optional(),
  requiresDentist: z.boolean(),
  isOptional: z.boolean(),
  description: z.string().nullable().optional(),
  typicalDuration: z.string().max(64).nullable().optional(),
});

export type StepFormValues = z.infer<typeof StepFormSchema>;

/**
 * Returns a list of human-readable validation errors for a raw step object.
 * Used to highlight invalid step rows in StepsManager before API calls are made.
 */
export function getStepValidationErrors(step: {
  name: string;
  feePaise: number;
  durationMinutes?: number | null;
}): string[] {
  const result = StepFormSchema.safeParse({
    name: step.name?.trim() ?? '',
    feePaise: step.feePaise,
    durationMinutes: step.durationMinutes ?? undefined,
    requiresDentist: true,
    isOptional: false,
  });
  if (result.success) return [];
  return result.error.issues.map((issue) => issue.message);
}
